import {
	AI_REASONING_EFFORTS,
	type AiModelId,
	type AiReasoningEffort,
	type PlatformTenantDetails as PlatformTenantDetailsPayload,
	type TenantInvitation,
} from '@livechat/shared';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import {
	getPlatformTenant,
	updateCustomerOpenRouter,
	inviteMember,
	listMembers,
	updatePlatformTenant,
	updatePlatformTenantAiAgents,
	updatePlatformTenantAiChatRating,
	updatePlatformTenantAiCopilot,
	updatePlatformTenantMessageEnhance,
} from '../api.js';

interface PlatformTenantDetailsProps {
	onBack: () => void;
	tenantId: string;
}

const reasoningEffortLabels: Record<AiReasoningEffort, string> = {
	high: 'High',
	low: 'Low',
	max: 'Maximum',
	medium: 'Medium',
	none: 'None',
	xhigh: 'Extra high',
};

function AiModelFields({
	excludedProviders = [],
	minimumThroughput,
	model,
	provider,
	reasoningEffort,
}: {
	excludedProviders?: string[];
	minimumThroughput: number | null;
	model: AiModelId;
	provider: string;
	reasoningEffort: AiReasoningEffort;
}) {
	const [selectedReasoningEffort, setSelectedReasoningEffort] = useState(reasoningEffort);

	return (
		<>
			<label className="owner-settings-field">
				<span>AI model</span>
				<input
					autoComplete="off"
					defaultValue={model}
					maxLength={200}
					name="model"
					placeholder="openai/gpt-oss-120b"
					required
					spellCheck={false}
				/>
				<small>Enter an OpenRouter model ID.</small>
			</label>

			<label className="owner-settings-field">
				<span>Provider</span>
				<input
					autoComplete="off"
					defaultValue={provider}
					maxLength={100}
					name="provider"
					placeholder="cerebras"
					spellCheck={false}
				/>
				<small>
					Optional. A named provider is tried first, then unavailable requests fall back
					to the cheapest compatible provider.
				</small>
			</label>

			<label className="owner-settings-field">
				<span>Exclude providers</span>
				<input
					autoComplete="off"
					defaultValue={excludedProviders.join(', ')}
					maxLength={2048}
					name="excludedProviders"
					placeholder="cerebras, groq"
					spellCheck={false}
				/>
				<small>
					Comma-separated OpenRouter provider slugs. Excluded providers are never used,
					including during fallback. Leave blank to clear.
				</small>
			</label>

			<label className="owner-settings-field">
				<span>Minimum throughput</span>
				<input
					defaultValue={minimumThroughput ?? ''}
					max={100_000}
					min={1}
					name="minimumThroughput"
					placeholder="150"
					type="number"
				/>
				<small>
					Optional tokens-per-second preference for cheapest-provider routing and
					fallback.
				</small>
			</label>

			<label className="owner-settings-field">
				<span>Reasoning effort</span>
				<select
					name="reasoningEffort"
					onChange={(event) =>
						setSelectedReasoningEffort(event.currentTarget.value as AiReasoningEffort)
					}
					value={selectedReasoningEffort}
				>
					{AI_REASONING_EFFORTS.map((effort) => (
						<option key={effort} value={effort}>
							{reasoningEffortLabels[effort]}
						</option>
					))}
				</select>
				<small>Choose an effort supported by the selected OpenRouter model.</small>
			</label>
		</>
	);
}

function optionalThroughput(data: FormData): number | null {
	const value = String(data.get('minimumThroughput')).trim();
	return value ? Number(value) : null;
}

function invitationUrl(invitation: TenantInvitation): string {
	const url = new URL(window.location.href);
	url.search = '';
	url.searchParams.set(
		invitation.kind === 'membership' ? 'membership' : 'setup',
		invitation.kind === 'membership' ? invitation.invitationToken : invitation.setupToken,
	);
	return url.toString();
}

export function PlatformTenantDetails({ onBack, tenantId }: PlatformTenantDetailsProps) {
	const customerGeneration = useRef({ generation: 0, tenantId });
	if (customerGeneration.current.tenantId !== tenantId) {
		customerGeneration.current = {
			generation: customerGeneration.current.generation + 1,
			tenantId,
		};
	}
	const [details, setDetails] = useState<PlatformTenantDetailsPayload>();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [savingOpenRouter, setSavingOpenRouter] = useState(false);
	const [savingMessageEnhance, setSavingMessageEnhance] = useState(false);
	const [savingAiAgents, setSavingAiAgents] = useState(false);
	const [savingAiChatRating, setSavingAiChatRating] = useState(false);
	const [savingAiCopilot, setSavingAiCopilot] = useState(false);
	const [invitingOwner, setInvitingOwner] = useState(false);
	const [ownerInvitation, setOwnerInvitation] = useState<TenantInvitation>();
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		setMessage(undefined);
		setInvitingOwner(false);
		setSavingOpenRouter(false);
		setOwnerInvitation(undefined);
		void getPlatformTenant(tenantId)
			.then((loaded) => {
				if (current) setDetails(loaded);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(caught instanceof Error ? caught.message : 'Could not load customer');
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [tenantId]);

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || saving) return;
		setSaving(true);
		setError(undefined);
		setMessage(undefined);
		const data = new FormData(event.currentTarget);
		try {
			const updated = await updatePlatformTenant(tenantId, {
				allowBrandingRemoval: data.get('allowBrandingRemoval') === 'on',
				companyName: String(data.get('companyName')),
				contactAddressLine1: String(data.get('contactAddressLine1')),
				contactAddressLine2: String(data.get('contactAddressLine2')),
				contactCity: String(data.get('contactCity')),
				contactCountry: String(data.get('contactCountry')),
				contactEmail: String(data.get('contactEmail')),
				contactName: String(data.get('contactName')),
				contactNotes: String(data.get('contactNotes')),
				contactPostcode: String(data.get('contactPostcode')),
				contactState: String(data.get('contactState')),
				contactTelephone: String(data.get('contactTelephone')),
				planCode: String(data.get('planCode')),
				status: String(data.get('status')) as PlatformTenantDetailsPayload['status'],
			});
			setDetails((current) =>
				current?.id === updated.id
					? { ...updated, memberCount: current.memberCount }
					: current,
			);
			setMessage('Customer details saved.');
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save customer');
		} finally {
			setSaving(false);
		}
	}

	async function saveOpenRouter(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || details.id !== tenantId || savingOpenRouter) return;
		const generation = customerGeneration.current;
		const apiKey = String(new FormData(event.currentTarget).get('apiKey') ?? '').trim();
		setSavingOpenRouter(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const saved = await updateCustomerOpenRouter(tenantId, apiKey);
			if (customerGeneration.current !== generation) return;
			setDetails((current) =>
				current?.id === tenantId ? { ...current, openRouterApiKey: saved.apiKey } : current,
			);
			setMessage('OpenRouter API key saved.');
		} catch {
			if (customerGeneration.current === generation)
				setError('Could not save OpenRouter API key.');
		} finally {
			if (customerGeneration.current === generation) setSavingOpenRouter(false);
		}
	}

	async function inviteOwner(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || invitingOwner) return;
		setInvitingOwner(true);
		setError(undefined);
		setMessage(undefined);
		setOwnerInvitation(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);
		const requestGeneration = customerGeneration.current;
		try {
			const invitation = await inviteMember(tenantId, {
				displayName: String(data.get('displayName')),
				email: String(data.get('email')),
				role: 'owner',
			});
			if (customerGeneration.current !== requestGeneration) return;
			setOwnerInvitation(invitation);
			form.reset();
			await listMembers(tenantId)
				.then((members) => {
					if (customerGeneration.current !== requestGeneration) return;
					setDetails((current) =>
						current?.id === tenantId
							? { ...current, memberCount: members.length }
							: current,
					);
				})
				.catch(() => undefined);
		} catch (caught) {
			if (customerGeneration.current === requestGeneration) {
				setError(caught instanceof Error ? caught.message : 'Could not invite the owner');
			}
		} finally {
			if (customerGeneration.current === requestGeneration) {
				setInvitingOwner(false);
			}
		}
	}

	async function copyOwnerInvitationLink(): Promise<void> {
		if (!ownerInvitation) return;
		try {
			await navigator.clipboard.writeText(invitationUrl(ownerInvitation));
			setMessage('Owner invitation link copied.');
		} catch {
			setError('Could not copy the owner invitation link');
		}
	}

	async function saveMessageEnhance(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || savingMessageEnhance) return;
		setSavingMessageEnhance(true);
		setError(undefined);
		setMessage(undefined);
		const data = new FormData(event.currentTarget);
		try {
			const messageEnhance = await updatePlatformTenantMessageEnhance(tenantId, {
				enabled: data.get('enabled') === 'on',
				minimumThroughput: optionalThroughput(data),
				excludedProviders: [
					...new Set(
						String(data.get('excludedProviders') ?? '')
							.split(',')
							.map((value) => value.trim())
							.filter(Boolean),
					),
				],
				model: String(data.get('model')) as AiModelId,
				provider: String(data.get('provider')),
				reasoningEffort: String(data.get('reasoningEffort')) as AiReasoningEffort,
			});
			setDetails((current) => (current ? { ...current, messageEnhance } : current));
			setMessage('Message Enhance settings saved for this customer.');
		} catch (caught) {
			setError(
				caught instanceof Error
					? caught.message
					: 'Could not save Message Enhance settings',
			);
		} finally {
			setSavingMessageEnhance(false);
		}
	}

	async function saveAiAgents(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || savingAiAgents) return;
		setSavingAiAgents(true);
		setError(undefined);
		setMessage(undefined);
		const data = new FormData(event.currentTarget);
		try {
			const aiAgents = await updatePlatformTenantAiAgents(tenantId, {
				enabled: data.get('enabled') === 'on',
				minimumThroughput: optionalThroughput(data),
				excludedProviders: [
					...new Set(
						String(data.get('excludedProviders') ?? '')
							.split(',')
							.map((value) => value.trim())
							.filter(Boolean),
					),
				],
				model: String(data.get('model')) as AiModelId,
				provider: String(data.get('provider')),
				reasoningEffort: String(data.get('reasoningEffort')) as AiReasoningEffort,
			});
			setDetails((current) => (current ? { ...current, aiAgents } : current));
			setMessage('AI Agent settings saved for this customer.');
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save AI Agent settings');
		} finally {
			setSavingAiAgents(false);
		}
	}

	async function saveAiCopilot(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || savingAiCopilot) return;
		setSavingAiCopilot(true);
		setError(undefined);
		setMessage(undefined);
		const data = new FormData(event.currentTarget);
		try {
			const aiCopilot = await updatePlatformTenantAiCopilot(tenantId, {
				enabled: data.get('enabled') === 'on',
				minimumThroughput: optionalThroughput(data),
				excludedProviders: [
					...new Set(
						String(data.get('excludedProviders') ?? '')
							.split(',')
							.map((value) => value.trim())
							.filter(Boolean),
					),
				],
				model: String(data.get('model')) as AiModelId,
				provider: String(data.get('provider')),
				reasoningEffort: String(data.get('reasoningEffort')) as AiReasoningEffort,
			});
			setDetails((current) => (current ? { ...current, aiCopilot } : current));
			setMessage('AI Copilot settings saved for this customer.');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save AI Copilot settings',
			);
		} finally {
			setSavingAiCopilot(false);
		}
	}

	async function saveAiChatRating(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!details || savingAiChatRating) return;
		setSavingAiChatRating(true);
		setError(undefined);
		setMessage(undefined);
		const data = new FormData(event.currentTarget);
		try {
			const aiChatRating = await updatePlatformTenantAiChatRating(tenantId, {
				enabled: data.get('enabled') === 'on',
				minimumThroughput: optionalThroughput(data),
				excludedProviders: [
					...new Set(
						String(data.get('excludedProviders') ?? '')
							.split(',')
							.map((value) => value.trim())
							.filter(Boolean),
					),
				],
				model: String(data.get('model')) as AiModelId,
				provider: String(data.get('provider')),
				reasoningEffort: String(data.get('reasoningEffort')) as AiReasoningEffort,
			});
			setDetails((current) => (current ? { ...current, aiChatRating } : current));
			setMessage('AI Chat Rating settings saved for this customer.');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save AI Chat Rating settings',
			);
		} finally {
			setSavingAiChatRating(false);
		}
	}

	return (
		<section className="workspace-section customer-detail-page">
			<div className="customer-detail-back-row">
				<button className="text-button customer-back-button" onClick={onBack} type="button">
					<span aria-hidden="true">←</span> Customers
				</button>
			</div>
			<div className="section-heading customer-detail-heading">
				<div>
					<p className="eyebrow">Platform administration</p>
					<h2>{details?.name ?? 'Customer details'}</h2>
					<p className="muted">Manage this customer workspace and its AI access.</p>
				</div>
				{details && (
					<span className={`status-pill ${details.status}`}>{details.status}</span>
				)}
			</div>

			{error && <div className="alert error customer-detail-alert">{error}</div>}
			{message && <div className="alert success customer-detail-alert">{message}</div>}

			{loading || details?.id !== tenantId ? (
				<div className="empty-configuration">Loading customer details…</div>
			) : details ? (
				<div className="customer-detail-panels">
					<form
						className="customer-detail-column"
						key={details.id}
						onSubmit={(event) => void save(event)}
					>
						<section className="settings-card integration-panel customer-details-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Customer</h4>
								<p>Manage the customer workspace, plan and access state.</p>
							</div>

							<div className="customer-facts" aria-label="Customer summary">
								<div>
									<span>Owner</span>
									<strong>{details.owner?.displayName ?? 'No owner'}</strong>
									<small>{details.owner?.email ?? 'No owner email'}</small>
								</div>
								<div>
									<span>Team members</span>
									<strong>{details.memberCount}</strong>
									<small>
										{details.owner
											? `Owner ${details.owner.status}`
											: 'Owner not set'}
									</small>
								</div>
								<div>
									<span>Created</span>
									<strong>
										{new Date(details.createdAt).toLocaleDateString()}
									</strong>
									<small>
										{new Date(details.createdAt).toLocaleTimeString()}
									</small>
								</div>
							</div>

							<div className="customer-fields">
								<label className="owner-settings-field customer-field-wide">
									<span>Customer ID</span>
									<input aria-label="Customer ID" readOnly value={details.id} />
									<small>Internal platform identifier for this customer.</small>
								</label>
								<label className="owner-settings-field">
									<span>Plan</span>
									<select defaultValue={details.planCode} name="planCode">
										<option value="starter">Starter</option>
										<option value="growth">Growth</option>
										<option value="enterprise">Enterprise</option>
									</select>
								</label>
								<label className="owner-settings-field">
									<span>Status</span>
									<select defaultValue={details.status} name="status">
										<option value="active">Active</option>
										<option value="suspended">Suspended</option>
										<option value="closed">Closed</option>
									</select>
								</label>
								<label className="owner-settings-field customer-field-wide">
									<span>Notes</span>
									<textarea
										defaultValue={details.contactNotes ?? ''}
										maxLength={5_000}
										name="contactNotes"
										rows={5}
									/>
								</label>
							</div>
						</section>

						<section className="settings-card integration-panel customer-address-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Address</h4>
								<p>Store the customer’s contact and postal details.</p>
							</div>

							<div className="customer-address-fields">
								<label className="owner-settings-field">
									<span>Name</span>
									<input
										defaultValue={details.contactName}
										maxLength={160}
										name="contactName"
										required
									/>
								</label>
								<label className="owner-settings-field">
									<span>Company</span>
									<input
										defaultValue={details.companyName ?? ''}
										maxLength={160}
										name="companyName"
										placeholder="Optional"
									/>
								</label>
								<label className="owner-settings-field customer-field-wide">
									<span>Line 1</span>
									<input
										defaultValue={details.contactAddressLine1 ?? ''}
										maxLength={255}
										name="contactAddressLine1"
									/>
								</label>
								<label className="owner-settings-field customer-field-wide">
									<span>Line 2</span>
									<input
										defaultValue={details.contactAddressLine2 ?? ''}
										maxLength={255}
										name="contactAddressLine2"
									/>
								</label>
								<label className="owner-settings-field">
									<span>City</span>
									<input
										defaultValue={details.contactCity ?? ''}
										maxLength={160}
										name="contactCity"
									/>
								</label>
								<label className="owner-settings-field">
									<span>State</span>
									<input
										defaultValue={details.contactState ?? ''}
										maxLength={160}
										name="contactState"
									/>
								</label>
								<label className="owner-settings-field">
									<span>Postcode</span>
									<input
										defaultValue={details.contactPostcode ?? ''}
										maxLength={40}
										name="contactPostcode"
									/>
								</label>
								<label className="owner-settings-field">
									<span>Country</span>
									<input
										defaultValue={details.contactCountry ?? ''}
										maxLength={160}
										name="contactCountry"
									/>
								</label>
								<label className="owner-settings-field">
									<span>Telephone</span>
									<input
										defaultValue={details.contactTelephone ?? ''}
										maxLength={80}
										name="contactTelephone"
										type="tel"
									/>
								</label>
								<label className="owner-settings-field">
									<span>Email</span>
									<input
										defaultValue={details.contactEmail ?? ''}
										maxLength={320}
										name="contactEmail"
										type="email"
									/>
								</label>
							</div>
						</section>

						<section className="settings-card integration-panel customer-branding-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Branding</h4>
								<p>Control whether this customer may remove platform branding.</p>
							</div>

							<label className="switch-row compact-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">
										Allow this customer to remove branding
									</span>
									<span className="settings-option-description">
										Shows Remove branding in this customer’s Chatbox settings.
									</span>
								</div>
								<input
									defaultChecked={details.allowBrandingRemoval}
									name="allowBrandingRemoval"
									type="checkbox"
								/>
							</label>

							<button
								className="primary-button customer-panel-action"
								disabled={saving}
							>
								{saving ? 'Saving…' : 'Save customer'}
							</button>
						</section>
					</form>

					<div className="customer-detail-column">
						<form
							className="settings-card integration-panel customer-owner-panel"
							onSubmit={(event) => void inviteOwner(event)}
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title">Company owners</h4>
								<p>
									Invite another owner without joining this customer’s workspace.
									Only the Manorsoft platform owner can perform this action.
								</p>
							</div>

							{ownerInvitation && (
								<div className="setup-result customer-owner-invitation">
									<div>
										<strong>
											Invitation created for {ownerInvitation.email}
										</strong>
										<p>
											{ownerInvitation.emailDelivery === 'queued'
												? 'The invitation email has been queued.'
												: 'Email delivery is not available, so send this link privately.'}{' '}
											{ownerInvitation.kind === 'membership'
												? 'They will sign in with their existing account to accept it. '
												: 'They will use it to create their account. '}
											It expires on{' '}
											{new Date(ownerInvitation.expiresAt).toLocaleString()}.
										</p>
									</div>
									<div className="setup-link-row">
										<input
											aria-label="Owner invitation link"
											readOnly
											value={invitationUrl(ownerInvitation)}
										/>
										<button
											className="secondary-button"
											onClick={() => void copyOwnerInvitationLink()}
											type="button"
										>
											Copy link
										</button>
									</div>
								</div>
							)}

							<div className="customer-fields">
								<label className="owner-settings-field">
									<span>Name</span>
									<input maxLength={160} name="displayName" required />
								</label>
								<label className="owner-settings-field">
									<span>Email address</span>
									<input maxLength={320} name="email" required type="email" />
								</label>
							</div>

							<button
								className="primary-button customer-panel-action"
								disabled={invitingOwner || details.status !== 'active'}
							>
								{invitingOwner ? 'Inviting…' : 'Invite owner'}
							</button>
							{details.status !== 'active' && (
								<small>Activate this customer before inviting another owner.</small>
							)}
						</form>

						<form
							className="settings-card integration-panel customer-openrouter-panel"
							key={tenantId}
							onSubmit={(event) => void saveOpenRouter(event)}
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title">Openrouter</h4>
							</div>
							<label className="owner-settings-field">
								<span>API key</span>
								<input
									name="apiKey"
									type="text"
									autoComplete="off"
									spellCheck={false}
									defaultValue={details.openRouterApiKey ?? ''}
									maxLength={512}
									placeholder="sk-or-..."
								/>
							</label>
							<button
								className="primary-button customer-panel-action"
								disabled={savingOpenRouter}
							>
								{savingOpenRouter ? 'Saving.' : 'Save OpenRouter'}
							</button>
						</form>
						<form
							className="settings-card integration-panel customer-message-enhance-panel"
							key={`message-enhance-${details.messageEnhance.enabled}-${details.messageEnhance.model}-${details.messageEnhance.provider}-${details.messageEnhance.minimumThroughput ?? ''}-${details.messageEnhance.reasoningEffort}-${details.messageEnhance.excludedProviders?.join(',') ?? ''}`}
							onSubmit={(event) => void saveMessageEnhance(event)}
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title">AI Message Enhance</h4>
								<p>
									Choose the model and make Message Enhance available to this
									customer.
								</p>
							</div>

							<AiModelFields
								excludedProviders={details.messageEnhance.excludedProviders}
								minimumThroughput={details.messageEnhance.minimumThroughput}
								model={details.messageEnhance.model}
								provider={details.messageEnhance.provider}
								reasoningEffort={details.messageEnhance.reasoningEffort}
							/>

							<label className="switch-row compact-switch owner-service-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">
										Enable Message Enhance
									</span>
									<span className="settings-option-description">
										Allow this customer’s administrators and supervisors to
										enable the service for their company.
									</span>
								</div>
								<input
									defaultChecked={details.messageEnhance.enabled}
									name="enabled"
									type="checkbox"
								/>
							</label>

							<button
								className="primary-button customer-panel-action"
								disabled={savingMessageEnhance}
							>
								{savingMessageEnhance ? 'Saving…' : 'Save Message Enhance'}
							</button>
						</form>

						<form
							className="settings-card integration-panel customer-ai-agents-panel"
							key={`ai-agents-${details.aiAgents.enabled}-${details.aiAgents.model}-${details.aiAgents.provider}-${details.aiAgents.minimumThroughput ?? ''}-${details.aiAgents.reasoningEffort}-${details.aiAgents.excludedProviders?.join(',') ?? ''}`}
							onSubmit={(event) => void saveAiAgents(event)}
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title">AI Agents</h4>
								<p>
									Choose the response model and allow this customer to add AI team
									members.
								</p>
							</div>
							<AiModelFields
								excludedProviders={details.aiAgents.excludedProviders}
								minimumThroughput={details.aiAgents.minimumThroughput}
								model={details.aiAgents.model}
								provider={details.aiAgents.provider}
								reasoningEffort={details.aiAgents.reasoningEffort}
							/>
							<label className="switch-row compact-switch owner-service-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">Enable AI Agents</span>
									<span className="settings-option-description">
										Allow this customer to enable AI team members.
									</span>
								</div>
								<input
									defaultChecked={details.aiAgents.enabled}
									name="enabled"
									type="checkbox"
								/>
							</label>
							<button
								className="primary-button customer-panel-action"
								disabled={savingAiAgents}
							>
								{savingAiAgents ? 'Saving…' : 'Save AI Agents'}
							</button>
						</form>

						<form
							className="settings-card integration-panel customer-ai-chat-rating-panel"
							key={`ai-chat-rating-${details.aiChatRating.enabled}-${details.aiChatRating.model}-${details.aiChatRating.provider}-${details.aiChatRating.minimumThroughput ?? ''}-${details.aiChatRating.reasoningEffort}-${details.aiChatRating.excludedProviders?.join(',') ?? ''}`}
							onSubmit={(event) => void saveAiChatRating(event)}
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title">AI Chat Rating</h4>
								<p>
									Choose the OpenRouter model and make AI chat ratings available
									to this customer.
								</p>
							</div>
							<AiModelFields
								excludedProviders={details.aiChatRating.excludedProviders}
								minimumThroughput={details.aiChatRating.minimumThroughput}
								model={details.aiChatRating.model}
								provider={details.aiChatRating.provider}
								reasoningEffort={details.aiChatRating.reasoningEffort}
							/>
							<label className="switch-row compact-switch owner-service-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">
										Enable AI Chat Rating
									</span>
									<span className="settings-option-description">
										Allow this customer to select AI Chat Rating in Integration
										settings.
									</span>
								</div>
								<input
									defaultChecked={details.aiChatRating.enabled}
									name="enabled"
									type="checkbox"
								/>
							</label>
							<button
								className="primary-button customer-panel-action"
								disabled={savingAiChatRating}
							>
								{savingAiChatRating ? 'Saving…' : 'Save AI Chat Rating'}
							</button>
						</form>

						<form
							className="settings-card integration-panel customer-ai-copilot-panel"
							key={`ai-copilot-${details.aiCopilot.enabled}-${details.aiCopilot.model}-${details.aiCopilot.provider}-${details.aiCopilot.minimumThroughput ?? ''}-${details.aiCopilot.reasoningEffort}-${details.aiCopilot.excludedProviders?.join(',') ?? ''}`}
							onSubmit={(event) => void saveAiCopilot(event)}
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title">AI Copilot</h4>
								<p>
									Choose the model and allow this customer to offer draft-only
									Copilot assistance to human staff.
								</p>
							</div>
							<AiModelFields
								excludedProviders={details.aiCopilot.excludedProviders}
								minimumThroughput={details.aiCopilot.minimumThroughput}
								model={details.aiCopilot.model}
								provider={details.aiCopilot.provider}
								reasoningEffort={details.aiCopilot.reasoningEffort}
							/>
							<label className="switch-row compact-switch owner-service-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">Enable AI Copilot</span>
									<span className="settings-option-description">
										Allow this customer’s owners and administrators to enable
										Copilot for their company.
									</span>
								</div>
								<input
									defaultChecked={details.aiCopilot.enabled}
									name="enabled"
									type="checkbox"
								/>
							</label>
							<button
								className="primary-button customer-panel-action"
								disabled={savingAiCopilot}
							>
								{savingAiCopilot ? 'Saving…' : 'Save AI Copilot'}
							</button>
						</form>
					</div>
				</div>
			) : (
				<div className="empty-configuration">Customer details are unavailable.</div>
			)}
		</section>
	);
}
